import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../hooks/useToast';
import { useBusy } from '../hooks/useBusy';
import { useLoad } from '../hooks/useLoad';
import { isNotFound } from '../lib/httpError';
import { useAfterWrite } from '../hooks/useAfterWrite';
import { useConfirm } from '../hooks/useConfirm';
import { getAthlete, getMyAthlete, getTeams, reassignAthleteTeam, deleteAthleteRecord, reactivateAthleteRecord, unclaimAthleteRecord, removeAthleteFromTeam } from '../api/client';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuthStore } from '../store/auth';
import { useI18n } from '../i18n';
import { useLabels } from '../i18n/labels';
import AthleteAvatar from '../components/shared/AthleteAvatar';
import GenderBadge from '../components/shared/GenderBadge';
import EditAthleteModal from '../components/shared/EditAthleteModal';
import { athleteRecordActions } from '../components/shared/athleteRecordActions';
import MergeAthleteModal from '../components/shared/MergeAthleteModal';
import ClaimRecordModal from '../components/shared/ClaimRecordModal';
import ModalBackdrop from '../components/shared/ModalBackdrop';
import ModalHeader from '../components/shared/ModalHeader';
import ModalFooter from '../components/shared/ModalFooter';
import BackLink from '../components/shared/BackLink';
import LoadFailure from '../components/shared/LoadFailure';
import Skeleton from '../components/shared/Skeleton';
import { FacebookPill } from '../components/shared/FacebookPill';
import { PencilIcon, SwapIcon, ArchiveIcon, PersonIcon, DotIcon, CalendarIcon, ScaleIcon, GloveIcon, CheckIcon, HourglassIcon, PeopleIcon, MedalIcon } from '../components/shared/Icons';
import ErrorState from '../components/shared/ErrorState';
import { personName } from '../utils/personName';
import BoutHistoryList, { outcomeWithRound } from '../components/bout/BoutHistoryList';
import BoutStatusPill from '../components/bout/BoutStatusPill';
import { weightText } from '../utils/weight';
import type { AthleteBout, AthleteEntry, Team } from '../types';
import Field from '../components/shared/Field';

/** The result of a scored bout for the athlete of the page; null before the verdict. */
const athleteResult = (b: AthleteBout): 'won' | 'lost' | 'draw' | null =>
  b.status !== 'scored' || !b.winner ? null : b.winner === 'draw' ? 'draw' : b.winner === b.corner ? 'won' : 'lost';

/**
 * The page of one athlete, at `/athletes/:id` (#595, #199): the data of the
 * athlete card, the actions of an admin or a team manager, and the bouts of
 * the athlete with their results. The list is `Athletes.tsx`. The edit, the
 * reassign and the merge were buttons on each card of the list until
 * 2026-10-14 (operator: the actions are on the page, as for a tournament and
 * a team).
 */
export default function AthleteDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { ageShort } = useLabels();
  const { hasRole, user } = useAuthStore();
  const canReassign = hasRole('admin', 'team_manager');
  // The roles that open the page of an archived athlete: the name of the backend check.
  const canSeeInactive = hasRole('admin', 'team_manager');
  // #578 — the merge is admin only: it moves scored bouts, and nothing undoes it.
  const canMerge = hasRole('admin');
  // #658 — only an admin archives a record. A team manager still restores one.
  const canArchive = hasRole('admin');
  const { showError } = useToast();
  const { confirm } = useConfirm();
  const afterWrite = useAfterWrite();
  const [editing, setEditing] = useState(false);
  const [merging, setMerging] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const [reassignTeamId, setReassignTeamId] = useState('');
  // #605 — a write locks its button until the answer. Archive, Restore and
  // Unlink in the footer of the edit modal share one lock.
  const headerBusy = useBusy();
  const reassignBusy = useBusy();
  // No name in the title: the browser telemetry records the title of each
  // page view, and the name of a person must not go there (#452).
  usePageTitle(t.nav.athletes);

  // The athlete and the bouts. The hook keeps only the newest run: a click on an opponent
  // changes `id`, clears the page and starts the load of another athlete. A write below
  // reads again behind the page (`refresh`, through `afterWrite`).
  const record = useLoad(id ? () => getAthlete(id).then(r => r.data) : null, [id], {
    context: 'AthleteDetail:load',
    // A 404 is a stale link to one athlete: `LoadFailure` shows it as an answer.
    expected: isNotFound,
  });
  const athlete: AthleteEntry | null = record.data?.athlete ?? null;
  const bouts: AthleteBout[] = record.data?.bouts ?? [];
  const loadError = record.error;
  // #665 (bug 89) — the teams of the reassign picker. A failed read shows an error with Retry in the modal.
  const teams = useLoad(canReassign ? () => getTeams().then(r => r.data) : null, [], {
    context: 'AthleteDetail:teams',
    initial: [] as Team[],
  });
  const allTeams = teams.data;
  const teamsFailed = !!teams.error;
  // #658 — "This is me": a signed-in person with no athlete record claims a
  // record with no account here, also a record with no team. The claim of a
  // record with no team goes to an admin or a team manager. A coach or a
  // staff member opens many athlete pages, so the button is not for them.
  const mayClaim = !!user && !hasRole('admin', 'team_manager', 'tournament_manager', 'head_coach', 'coach');
  const [claiming, setClaiming] = useState(false);
  // Null until the answer, and while the person may not claim. The gate closes the read.
  const ownRecord = useLoad(mayClaim ? () => getMyAthlete().then(r => !!r.data) : null, [user?.id], {
    context: 'AthleteDetail:getMyAthlete',
  });
  const hasOwnRecord: boolean | null = ownRecord.data ?? null;

  if (!athlete) {
    if (loadError) {
      return (
        <div className="space-y-4">
          <BackLink to="/athletes" label={t.common.allAthletes} />
          <LoadFailure error={loadError} onRetry={record.reload}
            notFound={{ icon: <PersonIcon className="w-12 h-12" />, title: t.common.athleteNotFound, to: '/athletes', linkLabel: t.common.allAthletes }} />
        </div>
      );
    }
    return <div className="space-y-4"><Skeleton.List shape="section" count={2} /></div>;
  }

  const a = athlete;
  const fullName = personName(a.first_name, a.last_name);

  async function handleArchive() {
    if (!(await confirm({ message: t.teams.archiveConfirm.replace('{name}', fullName), confirmLabel: t.teams.archive, destructive: true }))) return;
    await headerBusy.run(async () => {
      try {
        await deleteAthleteRecord(a.id);
        setEditing(false);
        await afterWrite({ source: 'AthleteDetail:archived', success: t.teams.archived.replace('{name}', fullName), refresh: record.refresh });
      } catch (err) { showError(err); }
    });
  }

  async function handleRestore() {
    if (!(await confirm({ message: t.teams.restoreConfirm.replace('{name}', fullName), confirmLabel: t.teams.restore }))) return;
    await headerBusy.run(async () => {
      try {
        await reactivateAthleteRecord(a.id);
        setEditing(false);
        await afterWrite({ source: 'AthleteDetail:restored', success: t.teams.restored.replace('{name}', fullName), refresh: record.refresh });
      } catch (err) { showError(err); }
    });
  }

  async function handleRemoveFromTeam() {
    if (!(await confirm({ message: t.teams.removeAthleteConfirm.replace('{name}', fullName).replace('{team}', a.team_name ?? ''), confirmLabel: t.teams.removeFromTeam, destructive: true }))) return;
    await headerBusy.run(async () => {
      try {
        await removeAthleteFromTeam(a.id);
        setEditing(false);
        await afterWrite({ source: 'AthleteDetail:removedFromTeam', success: t.teams.removedFromTeam.replace('{name}', fullName), refresh: record.refresh });
      } catch (err) { showError(err); }
    });
  }

  async function handleUnlink() {
    if (!(await confirm({ message: t.teams.unlinkConfirm.replace('{name}', fullName), confirmLabel: t.teams.unlink, destructive: true }))) return;
    await headerBusy.run(async () => {
      try {
        await unclaimAthleteRecord(a.id);
        setEditing(false);
        await afterWrite({ source: 'AthleteDetail:unlinked', success: t.teams.unlinked.replace('{name}', fullName), refresh: record.refresh });
      } catch (err) { showError(err); }
    });
  }

  async function handleReassign() {
    if (!reassignTeamId) return;
    const teamId = reassignTeamId;
    await reassignBusy.run(async () => {
      try {
        await reassignAthleteTeam(a.id, teamId);
        const newTeam = allTeams.find(c => c.id === teamId);
        setReassigning(false);
        await afterWrite({ source: 'AthleteDetail:reassign', success: `${fullName} → ${newTeam?.name || t.athletes.newTeam.toLowerCase()}`, refresh: record.refresh });
      } catch (err) {
        showError(err);
      }
    });
  }

  // The record counts the scored bouts by the side of the athlete.
  const scored = bouts.filter(b => b.status === 'scored' && b.winner);
  const won = scored.filter(b => b.winner === b.corner).length;
  const draws = scored.filter(b => b.winner === 'draw').length;
  const lost = scored.length - won - draws;
  const weight = weightText(a.current_weight_kg);

  // #675 — the record actions go to the footer of the Edit Athlete modal. The
  // role gate is here: a handler that is not set gives no button.
  const recordActions = athleteRecordActions(t, canReassign ? {
    onArchive: canArchive && a.is_active !== false ? handleArchive : undefined,
    onRestore: a.is_active === false ? handleRestore : undefined,
    // #658 — the record stays active and public, with no team.
    onRemoveFromTeam: a.is_active !== false && a.team_id ? handleRemoveFromTeam : undefined,
    onUnlink: a.claim_status === 'claimed' ? handleUnlink : undefined,
    onMerge: canMerge ? () => { setMerging(true); setEditing(false); } : undefined,
    disabled: headerBusy.busy,
  } : {});

  return (
    <div className="space-y-4">
      <BackLink to="/athletes" label={t.common.allAthletes} />

      <div className="card p-4">
        {/* #615 — the avatar, the name and the pills in one row; the bands of the team
            and of the state use the full width of the card, so a long team name
            keeps its width on a phone. */}
        <div className="flex items-start gap-4">
          <AthleteAvatar firstName={a.first_name || '?'} lastName={a.last_name || '?'} photoUrl={a.photo_url} size="lg" country={a.country} />
          <div className="flex-1 min-w-0">
            <h1 data-testid="athlete-detail-name" className="font-bold text-lg break-words">{fullName}</h1>
            {/* #615 — the bands of the tournament header: the pills of the
                athlete under the name, in the size of the tournament pills
                (12 px, 20 px tall, an icon); a line; the team; a line and the
                state and link pills, only when one shows. */}
            <div className="flex flex-wrap items-center gap-1.5 mt-1">
              {a.gender && <GenderBadge gender={a.gender} short size="sm" />}
              {a.age != null && a.age > 0 && <span className="badge badge-gray gap-1"><CalendarIcon className="w-3.5 h-3.5" />{ageShort(Math.floor(a.age))}</span>}
              {weight && <span className="badge badge-gray gap-1"><ScaleIcon className="w-3.5 h-3.5" />{weight}</span>}
              {/* #615 — the experience, not the bouts of the list below: the
                  coach enters the bouts before LMF, and each verdict adds 1. */}
              {a.experience_bouts != null && a.experience_bouts > 0 && <span data-testid="athlete-experience" className="badge badge-gray gap-1"><GloveIcon className="w-3.5 h-3.5" />{t.athletes.experiencePill.replace('{n}', String(a.experience_bouts))}</span>}
            </div>
          </div>
          {canReassign && (
            <div className="flex gap-1 shrink-0">
              <button type="button" onClick={() => setEditing(true)} data-testid="athlete-edit"
                className="btn-icon" title={t.common.edit} aria-label={t.common.edit}>
                <PencilIcon className="w-4 h-4" />
              </button>
              {a.is_active !== false && (
                <button type="button" onClick={() => { setReassigning(true); setReassignTeamId(a.team_id || ''); }} data-testid="athlete-reassign"
                  className="btn-icon" title={t.athletes.reassignTeam} aria-label={t.athletes.reassignTeam}>
                  <SwapIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>
        {hasOwnRecord === false && a.is_active !== false && a.claim_status !== 'claimed' && (
          <>
            <div className="border-t border-gray-100 my-3" />
            <button type="button" onClick={() => setClaiming(true)} data-testid="athlete-claim"
              className="btn-outline btn-sm">{t.teams.thisIsMe}</button>
          </>
        )}
        {a.team_id && a.team_name && (
          <>
            <div className="border-t border-gray-100 my-3" />
            <Link to={`/teams/${a.team_id}`} data-testid="athlete-detail-team" className="btn-outline btn-sm inline-flex items-center gap-1.5 max-w-full"><PeopleIcon className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{a.team_name}</span></Link>
          </>
        )}
        {(a.is_active === false || a.claim_status === 'claimed' || a.claim_status === 'pending' || a.facebook_url) && (
          <>
            <div className="border-t border-gray-100 my-3" />
            <div className="flex flex-wrap items-center gap-1.5">
              {a.is_active === false && <span className="badge badge-gray gap-1"><ArchiveIcon className="w-3.5 h-3.5" />{t.teams.archivedBadge}</span>}
              {a.claim_status === 'claimed' && <span className="badge badge-green gap-1"><CheckIcon className="w-3.5 h-3.5" />{t.teams.claimed}</span>}
              {a.claim_status === 'pending' && <span className="badge gap-1 bg-orange-50 text-orange-700"><HourglassIcon className="w-3.5 h-3.5" />{t.teams.claimsPending}</span>}
              {a.facebook_url && <FacebookPill url={a.facebook_url} testId="athlete-facebook" />}
            </div>
          </>
        )}
      </div>

      {/* The record: the scored bouts, by the side of the athlete. #198 —
          then the medals, beside it: the server sends the medal of each bout
          (lib/medals.ts), and the page counts what it received. */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="athlete-record">
        {([['won', t.athletes.won, won], ['lost', t.athletes.lost, lost], ['draw', t.common.draw, draws]] as const).map(([key, label, n]) => (
          <div key={key} className="card p-3 text-center" data-testid={`athlete-record-${key}`}>
            <div className="text-2xl font-black">{n}</div>
            <div className="text-xs text-gray-500">{label}</div>
          </div>
        ))}
        {([[1, t.athletes.gold], [2, t.athletes.silver], [3, t.athletes.bronze]] as const).map(([place, label]) => {
          const count = bouts.filter(b => b.medal === place).length;
          return (
            <div key={place} className="card p-3 text-center" data-testid={`athlete-medals-${place}`} data-count={count}>
              <div className="text-2xl font-black inline-flex items-center gap-1"><MedalIcon place={place} className="w-5 h-5" />{count}</div>
              <div className="text-xs text-gray-500">{label}</div>
            </div>
          );
        })}
      </div>

      {/* #631 — the row shell is BoutHistoryList, shared with the official
          page. The athlete row: the own corner and the opponent; the result,
          the outcome with the round in one pill, the medal. */}
      <BoutHistoryList
        title={t.athletes.history} empty={t.athletes.noBouts} bouts={bouts} rowTestId="athlete-bout"
        rowAttrs={b => ({ 'data-result': athleteResult(b) ?? b.status })}
        corners={b => (
          <>
            {/* One run of text: the own corner, "vs", the opponent and the
                team. A no-break space keeps "vs" with the name and the
                separator at the end of the name, so a wrap never leaves "vs"
                alone or starts a line with the separator (#631, F6). #627 —
                the page of an archived opponent opens only for an admin and a
                team manager (GET /api/athletes/:id); for the other users the
                name has no link. */}
            <span className="min-w-0 break-words">
              {/* The dot and "vs" never part at a wrap (review, 2026-10-21). */}
              <span className="whitespace-nowrap">
                <DotIcon className={`inline-block align-middle -mt-0.5 mr-1.5 w-4 h-4 ${b.corner === 'red' ? 'text-red-500' : 'text-blue-500'}`} />
                <span className="text-gray-500">{t.common.vs}{' '}</span>
              </span>
              {b.opponent_id && (b.opponent_is_active !== false || canSeeInactive)
                ? <Link to={`/athletes/${b.opponent_id}`} data-testid="athlete-bout-opponent" className="font-medium hover:underline">{personName(b.opponent_first_name, b.opponent_last_name)}</Link>
                : b.opponent_id
                  ? <span data-testid="athlete-bout-opponent" className="font-medium">{personName(b.opponent_first_name, b.opponent_last_name)}</span>
                  : <span className="text-gray-400">{t.common.tbd}</span>}
              {b.opponent_team_name && <span data-testid="athlete-bout-opponent-team" className="text-xs text-gray-400">{' ·'} {b.opponent_team_name}</span>}
            </span>
          </>
        )}
        pills={b => {
          const result = athleteResult(b);
          const outcome = result ? outcomeWithRound(b.outcome, b.round_ended) : null;
          return (
            <>
              {result === 'won' && <span className="badge badge-green">{t.common.winner}</span>}
              {result === 'lost' && <span className="badge badge-gray">{t.athletes.lost}</span>}
              {result === 'draw' && <span className="badge badge-gray">{t.common.draw}</span>}
              {outcome && <span data-testid="athlete-bout-outcome" data-round={b.round_ended ?? ''} className="badge badge-gray whitespace-nowrap">{outcome}</span>}
              {b.medal && (
                <span data-testid="athlete-bout-medal" data-medal={b.medal} className="badge badge-amber gap-1 whitespace-nowrap">
                  <MedalIcon place={b.medal} className="w-3.5 h-3.5" />{[t.athletes.gold, t.athletes.silver, t.athletes.bronze][b.medal - 1]}
                </span>
              )}
              {!result && <BoutStatusPill status={b.status} />}
            </>
          );
        }}
      />

      {editing && (
        <EditAthleteModal
          athlete={a}
          onSaved={() => { setEditing(false); void afterWrite({ source: 'AthleteDetail:edit', refresh: record.refresh }); }}
          onClose={() => setEditing(false)}
          recordActions={recordActions}
        />
      )}

      {claiming && (
        <ClaimRecordModal record={a} onClose={() => setClaiming(false)}
          onClaimed={() => { setClaiming(false); void afterWrite({ source: 'AthleteDetail:claim', success: t.teams.selfClaimSubmitted, refresh: record.refresh }); }} />
      )}

      {/* #578 — merge two records of one person. The kept record may be the other one. */}
      {merging && (
        <MergeAthleteModal
          athlete={a}
          onClose={() => setMerging(false)}
          onMerged={keptId => {
            setMerging(false);
            if (keptId !== a.id) navigate(`/athletes/${keptId}`, { replace: true });
            else void afterWrite({ source: 'AthleteDetail:merge', refresh: record.refresh });
          }}
        />
      )}

      {reassigning && (
        <ModalBackdrop onClose={() => setReassigning(false)} maxWidth="sm" ariaLabel={t.athletes.reassignAria}>
          <ModalHeader title={t.athletes.reassignTeam} subtitle={fullName} />
          <div className="space-y-3">
            <Field label={t.athletes.newTeam} htmlFor="reassign-team">
              {teamsFailed ? <ErrorState compact onRetry={teams.reload} /> : (
                <select id="reassign-team" className="input" value={reassignTeamId} onChange={e => setReassignTeamId(e.target.value)}>
                  <option value="">— {t.athletes.selectTeam} —</option>
                  {allTeams.map(c => (
                    <option key={c.id} value={c.id}>{c.name}{c.location ? ` · ${c.location}` : ''}</option>
                  ))}
                </select>
              )}
            </Field>
            {a.team_name && (
              <p className="text-xs text-gray-400">{t.athletes.currentTeam}: {a.team_name}</p>
            )}
          </div>
          <ModalFooter
            onSave={handleReassign}
            busy={reassignBusy.busy}
            saveLabel={t.athletes.reassign}
            saveDisabled={!reassignTeamId || reassignTeamId === a.team_id}
            onCancel={() => setReassigning(false)}
          />
        </ModalBackdrop>
      )}
    </div>
  );
}
